import { z } from "zod";
import { ROLE_NAMES, SCOPE_TYPES, type RoleName, type ScopeType } from "../permissions";
import { userStatusSchema } from "./auth";
import { signInMethodSchema } from "./signInMethod";
import { ACCOUNT_CREATION_CODES, ACCOUNT_CREATION_STATUSES } from "./accountCreation";

/** Users and access grants (FR-11, SEC-18). */

/**
 * SEC-18: the shortest reason a grant may state, trimmed. One constant for the grant dialog and
 * every grant body, so the client cannot accept what the server refuses (fix/grant-flow).
 */
export const GRANT_REASON_MIN_LENGTH = 10;

/** The roles whose grant always states a reason, on every surface (fix/grant-flow). */
export const REASON_REQUIRED_ROLES: readonly RoleName[] = [
  "PlatformAdmin",
  "TenantAdmin",
  "InstanceAdmin",
];

/**
 * Whether a grant must state a reason: a platform-scope grant, an admin-role grant, or any grant
 * through the platform surface. Decided by the role and scope, never by the tab it was made from;
 * the server applies the same rule in `AccessService.assertGrantable`.
 */
export function grantNeedsReason(
  roleName: RoleName,
  scopeType: ScopeType,
  platformSurface = false,
): boolean {
  return platformSurface || scopeType === "platform" || REASON_REQUIRED_ROLES.includes(roleName);
}

/** A grant's reason: trimmed, at least `GRANT_REASON_MIN_LENGTH` characters when present. */
export const grantReasonSchema = z
  .string()
  .trim()
  .min(GRANT_REASON_MIN_LENGTH, {
    error: `Say why, in at least ${GRANT_REASON_MIN_LENGTH} characters.`,
  })
  .max(500);

export const accessUsersQuerySchema = z.object({
  search: z.string().max(200).optional(),
  includeInactive: z
    .enum(["true", "false"])
    .default("false")
    .transform((v) => v === "true"),
});
export type AccessUsersQuery = z.infer<typeof accessUsersQuerySchema>;

export const accessGrantSummarySchema = z.object({
  id: z.uuid(),
  roleName: z.enum(ROLE_NAMES),
  scopeType: z.enum(SCOPE_TYPES),
  tenantId: z.uuid().nullable(),
  instanceId: z.uuid().nullable(),
  isActive: z.boolean(),
  grantedAt: z.string(),
  revokedAt: z.string().nullable(),
  /**
   * Group access (2026-09-28), additive. `group`: the default role a directory group carries (its
   * `id` is the enrolment, a `user_group_access` row, not a grant; it cannot be revoked here).
   * `override`: a grant an admin raised one member to, which lasts while they are in the group.
   * Absent for a direct grant.
   */
  source: z.enum(["group", "override"]).optional(),
  mappingId: z.uuid().nullable().optional(),
  mappingLabel: z.string().nullable().optional(),
});
export type AccessGrantSummary = z.infer<typeof accessGrantSummarySchema>;

/**
 * What the grant did about the invitation email (B-55): `queued` or `exists` (one is already
 * written for this grant), `email_not_configured`, or `user_disabled` (the person is deactivated,
 * so nothing was sent and a platform administrator must activate the account first).
 */
export const INVITE_OUTCOMES = [
  "queued",
  "exists",
  "email_not_configured",
  "user_disabled",
  "facts_invalid",
  // #1275: the person has signed in already, so there is nothing to invite them to.
  "user_active",
  // #1639 C0: the grant moved the person to Email sign-in, whose account a Skypoint operator
  // creates. The invitation waits, so it never points at an account that does not exist yet; the
  // admin sends it with Resend invitation once Skypoint says the account is made.
  "account_pending",
] as const;
export type InviteOutcome = (typeof INVITE_OUTCOMES)[number];

/** The body a grant answers: the grant, the grantee's id and address, and the invitation outcome. */
export type AccessGrantResult = AccessGrantSummary & {
  userId: string;
  email: string;
  /** Additive; absent from an older server. */
  inviteOutcome?: InviteOutcome;
  /** #1275: the grantee's status after the grant, so the screen says Pending only when it is. */
  userStatus?: "pending" | "active" | "disabled";
  /** #1275: the grant this one replaced (a role change at the same scope). */
  replaced?: { id: string; roleName: RoleName };
};

/**
 * Round 7, #1678: the answer to Resend invitation. `held`: the invitation waits for the account
 * skyData is making and goes out once it exists. `accountUnconfirmed`: it was sent to an Email
 * person whose account skyData did not make (the operator path), so the screen tells the admin to
 * ask Skypoint support to confirm the account if the person cannot sign in. Both optional so an
 * older server's answer still reads.
 */
export interface ResendInviteView {
  grantId: string;
  resendCount: number;
  held?: boolean;
  accountUnconfirmed?: boolean;
}

export const accessUserSchema = z.object({
  id: z.uuid(),
  email: z.string(),
  displayName: z.string().nullable(),
  status: userStatusSchema,
  lastSignInAt: z.string().nullable(),
  /**
   * Admin MFA (2026-09-29): whether the last recorded sign-in proved a second factor; null
   * before the first sign-in that recorded it. Optional so an older view still parses.
   */
  lastSignInMfa: z.boolean().nullable().optional(),
  /**
   * #1017: the person's last authenticated web request (`users.last_active_at`, written at most
   * once every 5 minutes; an MCP request does not move it). Null before their first request.
   * Answered only to a reader who can manage users (`instance.users.manage`, `tenant.users.manage`
   * or the platform surface); omitted below that, so absent means "not yours to see".
   */
  lastActiveAt: z.string().nullable().optional(),
  /**
   * Session 9: the issuer the row is bound to, and the issuer an unbound row is pinned to.
   * Issuer URLs, never an object id. Optional so a view built before S9 still parses.
   */
  idpIssuer: z.string().nullable().optional(),
  expectedIdpIssuer: z.string().nullable().optional(),
  /**
   * #1639 C0: how the person signs in, from the issuer they are bound or pinned to; null when
   * neither, or a provider the grant dialog does not offer. Optional so an older view parses.
   */
  signInMethod: signInMethodSchema.nullable().optional(),
  /**
   * #1639 C0: an Email person pending their first sign-in to whom no invitation was written yet
   * (it waits for the account Skypoint creates); the admin sends it with Resend invitation.
   */
  inviteWaiting: z.boolean().optional(),
  /**
   * #1678: an Email person pending their first sign-in whose invitation went out while skyData
   * made no account for them (none was asked for, or the request read `not_configured`, the
   * operator path). The row keeps a line telling the admin to ask Skypoint support to confirm the
   * account if the person cannot sign in. Absent otherwise.
   */
  inviteUnconfirmed: z.boolean().optional(),
  /** Group access: `group` for a row created at first sign-in by a directory group. */
  createdVia: z.enum(["grant", "group"]).optional(),
  /** Group access: what the person's last recorded token said about their groups. */
  groupsClaimState: z
    .enum(["present", "missing", "overage_resolved", "overage_unresolved"])
    .nullable()
    .optional(),
  /**
   * #1639 C1: the newest request that skyData create this person's "Sign in with email" account,
   * for a person who has not signed in yet. Absent when there is none (the flag off, or a
   * Microsoft sign-in). States and codes are `shared/types/accountCreation.ts`.
   */
  accountCreation: z
    .object({
      status: z.enum(ACCOUNT_CREATION_STATUSES),
      code: z.enum(ACCOUNT_CREATION_CODES).nullable(),
    })
    .optional(),
  grants: z.array(accessGrantSummarySchema),
});
export type AccessUser = z.infer<typeof accessUserSchema>;

/**
 * Session 9 (contract §5.4 step 5, O-Q21): only a platform administrator chooses the provider a
 * grant is pinned to, through `/platform/grants`. Every other grant body refuses
 * `expectedIssuer` with 400 rather than dropping it silently. The check reads the raw body and
 * then hands it to `schema`, so the parsed type carries no trace of the refused field.
 */
export function refusingExpectedIssuer<T extends z.ZodType>(schema: T) {
  return z
    .unknown()
    .superRefine((raw, ctx) => {
      if (
        raw !== null &&
        typeof raw === "object" &&
        (raw as Record<string, unknown>).expectedIssuer !== undefined
      ) {
        ctx.addIssue({
          code: "custom",
          path: ["expectedIssuer"],
          message: "The sign-in provider is chosen only through the platform grant route.",
        });
      }
    })
    .pipe(schema);
}

/**
 * FR-11: an admin grants access by email; there is no invitation. The scope columns that
 * must be present follow `scopeType`, mirroring the table's CHECK constraint.
 */
export const createGrantBodySchema = refusingExpectedIssuer(
  z
    .object({
      email: z.email().max(320),
      displayName: z.string().max(200).optional(),
      roleName: z.enum(ROLE_NAMES),
      scopeType: z.enum(SCOPE_TYPES),
      tenantId: z.uuid().optional(),
      instanceId: z.uuid().optional(),
      /** SEC-18: a platform-scope or admin-role grant states why; recorded in the audit metadata. */
      reason: grantReasonSchema.optional(),
      /** #1275: the grantor confirmed replacing the person's current role at this scope. */
      replaceExisting: z.boolean().optional(),
      /** #1639 C0: the dialog's "Sign in with"; absent keeps the server's pin (`grantPinIssuer`). */
      signInMethod: signInMethodSchema.optional(),
    })
    .superRefine((value, ctx) => {
      if (value.scopeType === "platform" && (value.tenantId || value.instanceId)) {
        ctx.addIssue({
          code: "custom",
          path: ["scopeType"],
          message: "A platform-scope grant names neither a tenant nor an instance.",
        });
      }
      if (value.scopeType === "tenant" && (!value.tenantId || value.instanceId)) {
        ctx.addIssue({
          code: "custom",
          path: ["tenantId"],
          message: "A tenant-scope grant names a tenant and no instance.",
        });
      }
      if (value.scopeType === "instance" && !value.instanceId) {
        ctx.addIssue({
          code: "custom",
          path: ["instanceId"],
          message: "An instance-scope grant names an instance.",
        });
      }
      // B-39: an instance row carries no tenant column; an unverified one would let the grant and
      // its audit row name a tenant the instance does not belong to.
      if (value.scopeType === "instance" && value.tenantId) {
        ctx.addIssue({
          code: "custom",
          path: ["tenantId"],
          message: "An instance-scope grant names the instance and no tenant.",
        });
      }
    }),
);
export type CreateGrantBody = z.infer<typeof createGrantBodySchema>;

export const revokeGrantBodySchema = z.object({
  reason: z.string().max(500).optional(),
});
export type RevokeGrantBody = z.infer<typeof revokeGrantBodySchema>;
